import React, { useEffect, useState } from "react";
import linkedin from "../../images/linkedin-logo.png";
import cv from "../../images/cv-icon.png";
import github from "../../images/github.png";
import Office from "../../images/office-icon.png";
import Languages from "../../images/languages-icon.png";
import VideoIntro from "../../images/video-icon-green.png";
import LoadingBlack from "../../images/loading-black.svg";
import jumpstart from "../../images/jumpstart_logo.jpeg";

/**
 * Returns a custom sponsorship/visa message depending on the "RTW doc" value.
 */
function getVisaMessage(visaStatus, fullName = "", undergradGradYear = null, postgradGradYear = null) {
  const firstName = fullName.split(" ")[0]; // e.g. "Jane Doe" -> "Jane"
  const jumpstartEmail = "operations@jumpstart-uk.com";
  const contactTeamMsg = `Contact the Jumpstart team if you have any concerns.`;

  // Handle array format - extract the first element if it's an array
  let status = visaStatus;
  if (Array.isArray(visaStatus)) {
    status = visaStatus.length > 0 ? visaStatus[0] : null;
  }

  // Return empty string if no status
  if (!status) {
    return "";
  }

  switch (status) {
    case "Yes - Post Study Visa":
      const uGradYear = undergradGradYear ? parseInt(undergradGradYear, 10) : null;
      const pGradYear = postgradGradYear ? parseInt(postgradGradYear, 10) : null;
      
      // If EITHER graduation year is 2024 or later, the candidate is recent, so we don't show the warning.
      if ((uGradYear && uGradYear >= 2024) || (pGradYear && pGradYear >= 2024)) {
        return ""; // Don't show for 2024+ grads with PSW
      }
      return `${firstName} has a two-year post-study visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "No - I require Visa sponsorship to work in the UK":
      return `${firstName} needs visa sponsorship to work in the UK. ${contactTeamMsg}`;
    case "Yes - High Potential Individual Visa":
      return `${firstName} has a two-year high performance individual visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "Yes - Skilled Worker Visa":
    case "No - Requires Visa sponsorship":
      return `${firstName} requires visa sponsorship to work in the UK. Contact the Jumpstart team for future details.`;
    case "Other":
      return `${firstName} may require visa sponsorship, please confirm directly with the Jumpstart team if concerned.`;
    case "No - I don't have the right to work in the UK":
      return `${firstName} does not have the right to work in the UK and requires visa sponsorship. ${contactTeamMsg}`;
    case "No - Looking for Visa sponsorship, but willing to self-sponsor if necessary":
      return `${firstName} is looking for visa sponsorship but willing to self-sponsor if necessary. ${contactTeamMsg}`;
    case "No - Requires visa sponsorship - is well versed in the process + has a lawyer at hand if needed":
      return `${firstName} requires visa sponsorship but is well versed in the process. ${contactTeamMsg}`;
    default:
      // If British or Irish passport, show nothing:
      if (
        status === "Yes - British Passport" ||
        status === "Yes - Irish Passport" ||
        status === "Yes - Settlement Status" ||
        status === "Yes - British National (Overseas) Visa" ||
        status === "Yes - Settlement Status / Indefinite Leave to Remain" ||
        status === "Yes - Dependent Visa (e.g., Spouse/Partner Visa)" ||
        status === "Yes - Ancestry Visa"
      ) {
        return "";
      }
      // Fallback for anything else:
      if (status) {
        const cleanedStatus = status.replace("Yes - ", "");
        return `${firstName} has ${cleanedStatus} and may require visa sponsorship in the future. ${contactTeamMsg}`;
      }
      return "";
  }
}

function ShortlistCard({
  handleAddSelectedCandidate,
  handleRemoveSelectedCandidate,
  selectedCandidates,
  candidate,
  onToggleCandidate,
  handleRemoveCandidate,
  role,
  removing,
  roles,
  onShortlistCandidate,
  shortlistRationale, // New prop for the AI rationale
  }) {
  const getColorClasses = (stream) => {
    switch (stream) {
      case "Generalist":
        return {
          bgColor: "bg-emerald-100",
          textColor: "text-emerald-700",
          workBgColor: "bg-emerald-50",
          fillColor: "fill-emerald-700",
          bgColor2: "bg-emerald-400",
        };
      case "Sales":
        return {
          bgColor: "bg-blue-100",
          textColor: "text-blue-700",
          workBgColor: "bg-blue-50",
          fillColor: "fill-blue-700",
          bgColor2: "bg-blue-400",
        };
      case "Software Engineering":
        return {
          bgColor: "bg-orange-100",
          textColor: "text-orange-700",
          workBgColor: "bg-orange-50",
          fillColor: "fill-orange-700",
          bgColor2: "bg-orange-400",
        };
      case "Marketing":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-700",
          workBgColor: "bg-purple-50",
          fillColor: "fill-purple-700",
          bgColor2: "bg-purple-400",
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          workBgColor: "bg-gray-50",
          fillColor: "fill-gray-700",
          bgColor2: "bg-gray-400",
        };
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return "";
    
    const years = Number(duration.years) || 0;
    const months = Number(duration.months) || 0;
    
    // Convert excess months to years
    const totalYears = years + Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    const parts = [];
    if (totalYears > 0) {
      parts.push(`${totalYears} yrs`);
    }
    if (remainingMonths > 0) {
      parts.push(`${remainingMonths} mos`);
    }
    
    return parts.length > 0 ? parts.join(', ') : '';
  };
  
  const [companyLink, setCompanyLink] = useState("");
  useEffect(() => {
    const linkFromStorage = localStorage.getItem("companyLink");
    setCompanyLink(linkFromStorage);
  }, []);

  const { bgColor, textColor, workBgColor, fillColor, bgColor2 } =
    getColorClasses(candidate.stream);

  const fallbackImageUrl =
    "https://cdn-icons-png.flaticon.com/512/4120/4120345.png";

  const isSelected = selectedCandidates.includes(candidate.id);

  const handleSelectToggle = () => {
    if (isSelected) {
      handleRemoveSelectedCandidate(role, candidate.id);
    } else {
      handleAddSelectedCandidate(role, candidate.id);
    }
  };

  const [copied, setCopied] = useState(false);
  const [showVisaTooltip, setShowVisaTooltip] = useState(false);
  const [showJumpstartNotes, setShowJumpstartNotes] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);

  const copyToClipboard = () => {
    let baseLink = companyLink || window.location.origin;
    try {
      const urlObj = new URL(baseLink);
      baseLink = urlObj.origin;
    } catch (err) {
      baseLink = window.location.origin;
    }
    const link = `${baseLink}/candidate/${candidate.url_id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  // Helper function to get the embedded video URL
  const getEmbeddedVideoUrl = (url) => {
    if (!url) return null;
    
    // Handle Loom videos
    if (url.includes('loom.com')) {
      // Convert from https://www.loom.com/share/[id] to https://www.loom.com/embed/[id]
      return url.replace('/share/', '/embed/');
    }
    
    // Handle Vimeo videos
    if (url.includes('vimeo.com')) {
      // Extract the Vimeo ID
      const vimeoId = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoId && vimeoId[1]) {
        return `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Handle YouTube videos
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract YouTube ID
      let youtubeId = '';
      if (url.includes('youtube.com/watch')) {
        const searchParams = new URL(url).searchParams;
        youtubeId = searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        youtubeId = url.split('youtu.be/')[1];
      }
      
      if (youtubeId) {
        return `https://www.youtube.com/embed/${youtubeId}`;
      }
    }
    
    // Return original URL if we can't transform it
    return url;
  };

  // Toggle video playback
  const toggleVideo = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    setIsVideoModalOpen(true);
    setIsVideoLoading(true);
  };
  
  // Handle video loaded
  const handleVideoLoaded = () => {
    setIsVideoLoading(false);
  };

  // Close video modal
  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setIsVideoLoading(false);
  };

  const workingStyleMapping = Array.isArray(candidate.working_style)
    ? candidate.working_style.map((style) => {
        if (style === "5 days a week in office") {
          return "Fully in office";
        } else if (style === "Hybrid (1-4 days in office)") {
          return "Hybrid";
        } else if (style === "Fully remote") {
          return "Fully remote";
        }
        return style;
      })
    : [];

  return (
    <div className="relative w-full h-full">
      {candidate.featured !== "Featured" && (
        <div className="absolute w-full md:w-[600px] top-0 left-0 p-4 rounded-md z-40 h-full bg-black bg-opacity-70 flex flex-col justify-center items-center">
          <div className="bg-white p-3 flex rounded-md w-4/5 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
              stroke="currentColor"
              className="size-5 md:size-6 font-bold"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m3 3 1.664 1.664M21 21l-1.5-1.5m-5.485-1.242L12 17.25 4.5 21V8.742m.164-4.078a2.15 2.15 0 0 1 1.743-1.342 48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185V19.5M4.664 4.664 19.5 19.5"
              />
            </svg>

            <p className="text-black font-bold text-sm md:text-base">
              Sorry, this candidate is no longer available
            </p>
          </div>
          <button
            onClick={() => handleRemoveCandidate(role, candidate.id)}
            className="px-4 md:px-8 py-2 w-4/5 mt-4 rounded-md bg-white text-black font-bold transition duration-200 hover:bg-gray-400 hover:text-white border-2 border-gray-400 text-sm md:text-base"
          >
            Remove
          </button>
        </div>
      )}
      <div
        className={`${
          isSelected ? "border-4 border-[#02B491]" : "border-4 border-white"
        } bg-white w-full md:w-[368px] rounded-t-md z-30 -mb-2 mt-5 py-2 flex items-center`}
      >
        <div className="flex items-center justify-between w-full px-2 md:px-4">
          {/* Select Button */}
          <div
            onClick={handleSelectToggle}
            className="flex items-center gap-x-1 md:gap-x-2 cursor-pointer"
          >
            <button
              className={`rounded-md items-center justify-center flex border-2 border-[#02B491] w-8 h-8 md:w-10 md:h-10 ${
                isSelected && "bg-[#02B491]/20"
              }`}
            >
              {isSelected && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6 md:size-8 text-[#02B491]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              )}
            </button>
            <p className="text-base md:text-lg">Select</p>
          </div>

          {/* Action Buttons Group */}
          <div className="flex items-center">
            <button
              onClick={copyToClipboard}
              className="hover:bg-gray-200 text-xs md:text-sm rounded-md p-1 md:p-2 flex items-center"
            >
              {copied ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4 md:size-5 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                  <span className="hidden sm:inline">Copied</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4 md:size-5 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 8.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v8.25A2.25 2.25 0 0 0 6 16.5h2.25m8.25-8.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-7.5A2.25 2.25 0 0 1 8.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 0 0-2.25 2.25v6"
                    />
                  </svg>
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </button>
            
            <button
              className="hover:bg-gray-200 text-xs md:text-sm rounded-md p-1 md:p-2 flex items-center"
              onClick={(e) => {
                e.stopPropagation(); // Prevent any parent event handlers
                if (removing === candidate.id) {
                  return;
                }
                handleRemoveCandidate(role, candidate.id);
              }}
              disabled={removing === candidate.id}
            >
              {removing === candidate.id ? (
                <>
                  <img
                    src={LoadingBlack}
                    className="h-5 md:h-6 fill-red-500"
                    alt="Loading"
                  />
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4 md:size-5 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                  <span className="hidden sm:inline">Remove</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="absolute z-30 w-full md:w-[360px] ml-1 border border-4 border-white"></div>
      <div
        className={`w-full md:w-[600px] z-0 mx-auto px-3 md:px-4 pt-2 pb-4 rounded-lg bg-white overflow-hidden flex flex-col h-full ${
          isSelected ? "border-4 border-[#02B491]" : "border-4 border-white"
        }`}
      >
        <div>
          <div className="flex flex-col md:flex-row md:gap-x-2">
            <div className="w-full md:w-32 h-32 flex justify-center md:block mb-2 md:mb-0">
              <div className="relative w-32 h-32">
                <img
                  className="w-32 h-32 object-cover rounded-lg"
                  src={candidate.image || fallbackImageUrl}
                  alt="Candidate"
                  onError={(e) => {
                    e.target.src = fallbackImageUrl;
                  }}
                />
                {/* Play button overlay - only show if candidate has a video */}
                {candidate.video && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={toggleVideo}
                  >
                    <div className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-300 transform hover:scale-110 shadow-lg">
                      <svg 
                        className="h-8 w-8 text-white" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="gap-y-1 flex flex-col max-w-full md:max-w-[420px]">
              <div className="flex items-center justify-center md:justify-start">
                <h5 className="text-lg md:text-xl font-bold tracking-tight text-gray-900">
                  {candidate.name}
                </h5>

                {candidate.linkedin && (
                  <img
                    src={linkedin}
                    alt={candidate.name}
                    title="Linkedin icon"
                    className="h-5 w-5 ml-2 rounded-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      let url = candidate.linkedin;
                      if (!url.startsWith("https://")) {
                        url = "https://" + url;
                      }
                      window.open(url, "_blank");
                    }}
                    style={{ cursor: "pointer" }}
                  />
                )}

                {candidate.cv && (
                  <img
                    src={cv}
                    alt={candidate.cv}
                    title="CV icon"
                    className="h-5 w-5 ml-2"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(candidate.cv, "_blank");
                    }}
                    style={{ cursor: "pointer" }}
                  />
                )}
                {candidate.github && (
                  <img
                    src={github}
                    alt={candidate.github}
                    title="Github icon"
                    className="h-5 w-5 ml-2"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(candidate.github, "_blank");
                    }}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </div>
              <div className="flex flex-wrap justify-center md:justify-start">
                {candidate.roles &&
                  candidate.roles.map((role, index) => (
                    <span
                      key={index}
                      className={`inline-flex mr-2 mb-2 items-center gap-x-1 md:gap-x-1.5 rounded-md px-1.5 md:px-2 py-1 text-xs md:text-sm font-medium ${bgColor} ${textColor}`}
                    >
                      {role}
                    </span>
                  ))}
                {candidate.sales && (
                  <span
                    className={`inline-flex mr-2 mb-2 items-center gap-x-1 md:gap-x-1.5 rounded-md px-1.5 md:px-2 py-1 text-xs md:text-sm font-medium ${bgColor} ${textColor}`}
                  >
                    {candidate.sales}
                  </span>
                )}
                {candidate.swe_roles &&
                  candidate.swe_roles.map((role, index) => (
                    <span
                      key={index}
                      className={`inline-flex mr-2 mb-2 items-center gap-x-1 md:gap-x-1.5 rounded-md px-1.5 md:px-2 py-1 text-xs md:text-sm font-medium ${bgColor} ${textColor}`}
                    >
                      {role.trim()}
                    </span>
                  ))}
                {candidate.time_coding && (
                  <span
                    className={`inline-flex mr-2 mb-2 items-center gap-x-1 md:gap-x-1.5 rounded-md px-1.5 md:px-2 py-1 text-xs md:text-sm font-medium ${bgColor} ${textColor}`}
                  >
                    {candidate.time_coding} yrs of coding exp
                  </span>
                )}
              </div>
              <div className="flex flex-col md:flex-row justify-center md:justify-start">
                {candidate.working_style &&
                  Array.isArray(candidate.working_style) &&
                  workingStyleMapping.length > 0 && (
                    <p className="pr-2 md:pr-4 font-normal flex text-xs md:text-sm text-black justify-center md:justify-start">
                      <img className="h-5 md:h-6 pr-1 pb-1" src={Office} />
                      {workingStyleMapping.join(" or ")}
                    </p>
                  )}
                {candidate.languages &&
                  Array.isArray(candidate.languages) &&
                  candidate.languages.some((language) => language !== "N/A") && (
                    <p className="font-normal flex text-xs md:text-sm text-black justify-center md:justify-start">
                      <img className="h-4 md:h-5 pr-1" src={Languages} />
                      {candidate.languages
                        .filter((language) => language !== "N/A")
                        .join(", ")}
                    </p>
                  )}
              </div>

              {/* Visa Status Component - Styled like the applications page */}
              {candidate.right_to_work &&
                getVisaMessage(
                  candidate.right_to_work,
                  candidate.name,
                  candidate.undergrad_graduation_year,
                  candidate.postgrad_graduation_year
                ) && (
                  <div className="mb-3 md:mb-4 mt-2 md:mt-0">
                    <div
                      className={`rounded-lg overflow-hidden transition-all duration-300 ${
                        showVisaTooltip
                          ? "shadow-md border border-gray-100"
                          : "border border-transparent hover:border-gray-100"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card navigation
                        setShowVisaTooltip(!showVisaTooltip);
                      }}
                      onMouseEnter={() => setShowVisaTooltip(true)}
                      onMouseLeave={() => setShowVisaTooltip(false)}
                    >
                      {/* Header */}
                      <div
                        className={`flex items-center justify-between p-2 md:p-3 cursor-pointer ${
                          showVisaTooltip
                            ? "bg-gradient-to-r from-gray-50 to-gray-100/30"
                            : "bg-gray-50/60 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center space-x-1 md:space-x-2">
                          <div className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded bg-gray-100">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-3 h-3 md:w-4 md:h-4 text-gray-600"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
                              />
                            </svg>
                          </div>
                          <span className="font-medium text-xs md:text-sm text-gray-800">
                            Visa Status
                          </span>
                        </div>
                        <div
                          className={`rounded-full p-1 ${
                            showVisaTooltip ? "bg-gray-100/80" : "bg-transparent"
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className={`w-3 h-3 md:w-4 md:h-4 text-gray-600 transition-transform duration-300 ${
                              showVisaTooltip ? "rotate-180 transform" : ""
                            }`}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Content - animate height */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          showVisaTooltip ? "max-h-96" : "max-h-0"
                        }`}
                      >
                        <div className="p-2 md:p-3 pt-1 md:pt-2 bg-white border-t border-gray-100">
                          <p
                            className="text-xs md:text-sm text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: getVisaMessage(
                                candidate.right_to_work,
                                candidate.name,
                                candidate.undergrad_graduation_year,
                                candidate.postgrad_graduation_year
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Expected Salary section - moved here to be with other key info */}
              <div className="mb-3 md:mb-4">
                <h5
                  className={`flex items-center text-xs md:text-sm tracking-tight text-gray-900 justify-center md:justify-start`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4 md:size-5 mr-1 md:mr-2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                    />
                  </svg>
                  <span>Expected Salary: </span>
                  {candidate.minimum_salary && (
                    <span className="text-xs md:text-sm font-semibold tracking-tight text-gray-900 ml-1 md:ml-2">
                      {candidate.minimum_salary} - {candidate.ideal_salary}
                      {candidate.commission_expectation &&
                        ` + ${candidate.commission_expectation} commission`}
                    </span>
                  )}
                </h5>
              </div>

              {/* Jumpstart's POV component - collapsible */}
              {candidate["Jumpstart view (Sales)"] && (
                <div className="mb-3 md:mb-4">
                  <div
                    className={`rounded-lg overflow-hidden transition-all duration-300 ${
                      showJumpstartNotes
                        ? "shadow-md border border-emerald-100"
                        : "border border-transparent hover:border-emerald-100"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card navigation
                      setShowJumpstartNotes(!showJumpstartNotes);
                    }}
                    onMouseEnter={() => setShowJumpstartNotes(true)}
                    onMouseLeave={() => setShowJumpstartNotes(false)}
                  >
                    {/* Header */}
                    <div
                      className={`flex items-center justify-between p-2 md:p-3 cursor-pointer ${
                        showJumpstartNotes
                          ? "bg-gradient-to-r from-emerald-50 to-emerald-100/30"
                          : "bg-emerald-50/60 hover:bg-emerald-50"
                      }`}
                    >
                      <div className="flex items-center space-x-1 md:space-x-2">
                        <div className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded bg-emerald-100">
                          <img
                            src={jumpstart}
                            alt="Jumpstart"
                            className="h-3 w-3 md:h-4 md:w-4"
                          />
                        </div>
                        <span className="font-medium text-xs md:text-sm text-gray-800">
                          Jumpstart's POV
                        </span>
                      </div>
                      <div
                        className={`rounded-full p-1 ${
                          showJumpstartNotes
                            ? "bg-emerald-100/80"
                            : "bg-transparent"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className={`w-3 h-3 md:w-4 md:h-4 text-emerald-600 transition-transform duration-300 ${
                            showJumpstartNotes ? "rotate-180 transform" : ""
                          }`}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Content - animate height */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        showJumpstartNotes ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="p-2 md:p-3 pt-1 md:pt-2 bg-white border-t border-emerald-100">
                        <p className="text-xs md:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                          {candidate["Jumpstart view (Sales)"]}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Recommendation Rationale component - elevated card treatment */}
          {shortlistRationale && shortlistRationale.rationale && (
            <div className="mb-3 md:mb-4">
              <div className="bg-emerald-50 rounded-lg border-l-4 border-[#02B491] p-3 shadow-lg">
                <div className="flex items-center mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#02B491] text-white shadow-sm">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed font-medium">
                  {shortlistRationale.rationale}
                </p>
                {shortlistRationale.addedBy && (
                  <p className="text-xs text-gray-500 mt-2">
                    Added by {shortlistRationale.addedBy} •{" "}
                    {new Date(shortlistRationale.addedAt).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-col justify-between">
            <div className="text-xs md:text-sm">
              {candidate.undergrad_degree_type && (
                <p className="text-xs md:text-sm">
                  <span className="font-semibold">
                    {candidate.undergrad_degree_type}{" "}
                    {candidate.undergrad_degree_subject}
                  </span>
                  {candidate.undergrad_university && " @ "}
                  {candidate.undergrad_university} ({candidate.undergrad_result}
                  {candidate.undergrad_result &&
                    candidate.undergrad_graduation_year &&
                    " - "}
                  {candidate.undergrad_graduation_year})
                </p>
              )}

              {candidate.postgrad_degree_type && (
                <p className="text-xs md:text-sm pt-1 md:pt-2">
                  <span className="font-semibold">
                    {candidate.postgrad_degree_type}{" "}
                    {candidate.postgrad_degree_subject}
                  </span>
                  {candidate.postgrad_university && " @ "}
                  {candidate.postgrad_university} ({candidate.postgrad_result}
                  {candidate.postgrad_result &&
                    candidate.postgrad_graduation_year &&
                    " - "}
                  {candidate.postgrad_graduation_year})
                </p>
              )}
            </div>

            <div className={`pb-2 mt-2 px-2 rounded-md ${workBgColor}`}>
              {candidate.recentTarget && (
                <div className="font-normal text-xs md:text-sm text-gray-700 pt-1 md:pt-2">
                  <span className="font-semibold text-black">
                    Recent Target Achieved:
                  </span>{" "}
                  {candidate.recentTarget}
                </div>
              )}

              {candidate.work_experience_1_company && (
                <>
                  <p className="text-xs md:text-sm pt-1 md:pt-2">
                    <span className="font-semibold">
                      {candidate.work_experience_1_role}
                    </span>{" "}
                    @ {candidate.work_experience_1_company}
                    {candidate.work_experience_1_duration &&
                      ` (${formatDuration(candidate.work_experience_1_duration)})`}
                  </p>
                  <p className="text-gray-500 text-xs md:text-sm">
                    {candidate.work_experience_1_description}
                  </p>
                </>
              )}

              {candidate.work_experience_2_company && (
                <>
                  <p className="text-xs md:text-sm pt-1 md:pt-2">
                    <span className="font-semibold">
                      {candidate.work_experience_2_role}
                    </span>{" "}
                    @ {candidate.work_experience_2_company}
                    {candidate.work_experience_2_duration &&
                      ` (${formatDuration(candidate.work_experience_2_duration)})`}
                  </p>
                  <p className="text-gray-500 text-xs md:text-sm">
                    {candidate.work_experience_2_description}
                  </p>
                </>
              )}
            </div>

            {candidate.description && (
              <p className="mt-2 font-normal text-xs md:text-sm text-gray-700 flex">
                <div className="mr-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4 md:size-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                    />
                  </svg>
                </div>

                {candidate.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && candidate.video && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeVideoModal}
        >
          <div 
            className="relative bg-white rounded-lg overflow-hidden max-w-4xl max-h-[90vh] w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {candidate.name} - Video Introduction
              </h3>
              <button 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={closeVideoModal}
              >
                <svg 
                  className="h-6 w-6 text-gray-600" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            {/* Video Container */}
            <div className="relative bg-black" style={{ paddingBottom: '56.25%', height: 0 }}>
              {isVideoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                  <svg className="animate-spin h-12 w-12 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
              <iframe
                src={getEmbeddedVideoUrl(candidate.video)}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`${candidate.name} video introduction`}
                onLoad={handleVideoLoaded}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortlistCard;